import { useServices } from '../context/WebsiteContext';
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, ChevronRight, Star, Phone, Clock, MapPin, ShoppingBag, Sparkles } from 'lucide-react';
import { useSmartSite, useSmartContent } from '../context/SmartSiteContext';
import { useIntentOrder, INTENT_LABELS } from '../hooks/useIntentOrder';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Build the curated FAQ lineup for the homepage.
 *
 * Goal: give every visitor 2-3 meaningful hooks across intents plus a couple of
 * universal ("general") questions — NEVER a wall of one category.
 *
 * Logic:
 *  - When we DON'T yet know the visitor's intent → mixed: 1 from each
 *    intent-bearing category (business-branding, school-uniforms, greek-apparel,
 *    personalized-gifts, graduations, printing, custom-embroidery) + 2 general,
 *    capped at 8.
 *  - When we DO know the top intent → 3 from that intent first, then 1 from
 *    each of the other intent-bearing categories, then fill with general,
 *    capped at 8.
 */
const HOMEPAGE_FAQ_COUNT = 8;
function buildHomepageFaqMix(allFaqs, intentResult) {
  if (!allFaqs || allFaqs.length === 0) return [];
  const byCat = new Map();
  for (const f of allFaqs) {
    const c = f.category || 'general';
    if (!byCat.has(c)) byCat.set(c, []);
    byCat.get(c).push(f);
  }

  const intentCats = [...byCat.keys()].filter(c => c !== 'general');
  const generalPool = [...(byCat.get('general') || [])];
  const picked = [];
  const seen = new Set();
  const pick = (f) => {
    if (f && !seen.has(f.id)) {
      picked.push(f);
      seen.add(f.id);
    }
  };

  const topIntent = intentResult?.topIntent;

  // 1) Prioritize top intent with 3 items
  if (topIntent && byCat.has(topIntent)) {
    byCat.get(topIntent).slice(0, 3).forEach(pick);
  }

  // 2) One per other intent-bearing category (round-robin, not alphabetical)
  const otherCats = intentCats.filter(c => c !== topIntent);
  for (const c of otherCats) {
    const pool = byCat.get(c) || [];
    if (pool.length > 0) pick(pool[0]);
    if (picked.length >= HOMEPAGE_FAQ_COUNT) break;
  }

  // 3) Fill remaining slots with general
  for (const g of generalPool) {
    if (picked.length >= HOMEPAGE_FAQ_COUNT) break;
    pick(g);
  }

  // 4) If still short (e.g. no general), fall back to next items in each category
  if (picked.length < HOMEPAGE_FAQ_COUNT) {
    for (const c of intentCats) {
      const pool = byCat.get(c) || [];
      for (const f of pool) {
        if (picked.length >= HOMEPAGE_FAQ_COUNT) break;
        pick(f);
      }
    }
  }

  return picked.slice(0, HOMEPAGE_FAQ_COUNT);
}

/* ── real team photos from original site ── */
const TEAM_PHOTOS = {
  gayle: 'https://shopkyis.com/wp-content/uploads/2024/10/DSC_1917b.webp',
  danielle: 'https://shopkyis.com/wp-content/uploads/2024/10/Danielle-JOhnson-Headshot.webp',
  gabrielle: 'https://shopkyis.com/wp-content/uploads/2024/10/Gabrielle-McLeod-Headshot.webp',
};

const HERO_IMAGE = '/images/custom-embroidery/hero.jpeg';

/* ── Intersection Observer hook (inline) ── */
function useReveal(threshold = 0.12, deps = []) {
  const ref = useRef(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      (entries) =>
        entries.forEach((e) => {
          if (e.isIntersecting) {
            e.target.classList.add('visible');
          }
        }),
      { threshold, rootMargin: '0px 0px -40px 0px' }
    );
    // observe the section + every revealable child
    const targets = [el, ...el.querySelectorAll('.reveal,.reveal-left,.reveal-right,.reveal-scale,.stagger-grid')];
    targets.forEach((t) => obs.observe(t));
    return () => obs.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [threshold, ...deps]);
  return ref;
}

/* ── Animated counter ── */
function AnimatedStat({ value, label }) {
  const [display, setDisplay] = useState('0');
  const ref = useRef(null);
  const started = useRef(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !started.current) {
          started.current = true;
          const numMatch = value.match(/(\d+)/);
          if (!numMatch) { setDisplay(value); return; }
          const target = parseInt(numMatch[1], 10);
          const suffix = value.replace(/\d+/, '');
          const dur = 1200;
          const start = performance.now();
          const tick = (now) => {
            const p = Math.min((now - start) / dur, 1);
            const ease = 1 - Math.pow(1 - p, 3);
            setDisplay(Math.floor(ease * target) + suffix);
            if (p < 1) requestAnimationFrame(tick);
          };
          requestAnimationFrame(tick);
        }
      },
      { threshold: 0.5 }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [value]);

  return (
    <div ref={ref} className="text-center">
      <p className="text-4xl sm:text-5xl font-extrabold text-white stat-number">{display}</p>
      <p className="text-sm text-white/75 mt-1 tracking-wide uppercase">{label}</p>
    </div>
  );
}

/* ═══════ HERO — configurable via Smart Site admin ═══════ */
const HERO_DEFAULTS = {
  eyebrow: 'Welcome To Kre8ives',
  heading: 'Hello ',
  highlight: 'Kre8ives',
  body: null, // null = use JSX fallback below
  body2: 'Committed to the growth and development of our community business partners, we continue to contribute to the success of our community by providing accessible and affordable branding opportunities.',
  cta_text: 'Get Started',
  cta_link: '/contact',
  secondary_cta_text: 'Request Quote',
  secondary_cta_link: '/quote/builder',
  image: HERO_IMAGE,
};

const HeroSection = ({ heroRef }) => {
  const { items } = useSmartContent('homepage-hero', 'hero');
  const overrides = items?.[0]?.data || {};
  const h = { ...HERO_DEFAULTS, ...Object.fromEntries(Object.entries(overrides).filter(([, v]) => v !== null && v !== '')) };

  return (
    <section
      ref={heroRef}
      className="relative min-h-[88vh] flex items-center overflow-hidden bg-[#1B3A5C]"
      data-testid="hero-section"
    >
      <div className="absolute inset-0 opacity-[0.04] pattern-dots" />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 w-full relative z-10">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          <div className="hero-stagger">
            <p className="animate-fade-in-up text-[#F5A623] text-sm font-semibold tracking-[0.2em] uppercase mb-4">
              {h.eyebrow}
            </p>
            <h1 className="animate-fade-in-up text-5xl sm:text-6xl lg:text-7xl font-extrabold tracking-tight leading-[1.05] mb-6 text-white">
              {h.heading}<span className="text-[#F5A623]">{h.highlight}</span>
            </h1>
            <p className="animate-fade-in-up text-lg text-blue-100/80 leading-relaxed mb-4">
              {h.body || (
                <><strong className="text-white">Kre8ing Your IdeaS</strong> is a family-owned embroidery
                company that cultivates the creativity of our customers through personalized apparel and
                accessories.</>
              )}
            </p>
            <p className="animate-fade-in-up text-blue-200/60 leading-relaxed mb-8">
              {h.body2}
            </p>
            <div className="animate-fade-in-up flex flex-col sm:flex-row gap-4">
              <Link
                to={h.cta_link}
                className="inline-flex items-center justify-center px-8 py-4 bg-[#F5A623] text-gray-900 font-bold rounded-full btn-glow"
                data-testid="hero-shop-btn"
              >
                {h.cta_text}
                <ArrowRight className="ml-2 w-5 h-5" />
              </Link>
              <Link
                to={h.secondary_cta_link}
                className="inline-flex items-center justify-center px-8 py-4 border-2 border-white/40 text-white font-bold rounded-full btn-outline-glow hover:bg-white/10"
                data-testid="hero-quote-btn"
              >
                {h.secondary_cta_text}
              </Link>
            </div>
          </div>

          <div className="relative hidden lg:block animate-fade-in-right">
            <div className="relative">
              <div className="absolute -top-6 -left-6 w-72 h-72 bg-[#F5A623]/10 rounded-full blur-3xl" />
              <div className="absolute -bottom-8 -right-8 w-64 h-64 bg-[#4A90A4]/10 rounded-full blur-3xl" />
              <img
                src={h.image}
                alt="KYIS Custom Embroidery"
                className="relative z-10 w-full h-auto rounded-3xl shadow-2xl animate-float"
                data-testid="hero-image"
              />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

/* ══════════════════════════════════════════ */
const HomePage = () => {
  const services = useServices().map(s=>({...s,description:s.shortDescription,href:s.button_link}));
  const [testimonials, setTestimonials] = useState([]);
  const [faqs, setFaqs] = useState([]);
  const [team, setTeam] = useState([]);

  const smart = useSmartSite();

  // Smart content hooks for dynamic sections
  const heroContent = useSmartContent('homepage-hero', 'hero');
  const servicesContent = useSmartContent('homepage-services', 'section_copy');
  const processContent = useSmartContent('homepage-process', 'section_copy');
  const proofContent = useSmartContent('homepage-proof', 'section_copy');
  const ctaContent = useSmartContent('homepage-cta', 'cta');

  const sc = (hook, key, fallback) => hook.items?.[0]?.data?.[key] || fallback;

  // Intent-driven featured products
  const [featuredProducts, setFeaturedProducts] = useState([]);
  const [featuredLoading, setFeaturedLoading] = useState(false);
  const intents = smart?.intents || {};
  const hasIntents = Object.keys(intents).length > 0;
  const featuredRef = useReveal(0.08, [featuredProducts.length]);

  const heroRef = useReveal(0.1);
  const statsRef = useReveal(0.2);
  const servicesRef = useReveal(0.08);
  const processRef = useReveal(0.1);
  const teamRef = useReveal(0.1, [team.length]);
  const testimonialsRef = useReveal(0.1, [testimonials.length]);
  const faqRef = useReveal(0.1, [faqs.length]);
  const ctaRef = useReveal(0.2);

  // Intent-aware ordering for FAQs & Testimonials
  const orderedFaqs = useIntentOrder(faqs, 'category');
  const orderedTestimonials = useIntentOrder(testimonials, 'surface');

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    // Fire each fetch independently — Promise.all sometimes stalls when the
    // HTTP/1.1 connection pool is saturated by SmartSiteProvider's parallel
    // content requests. Independent fires avoid the "all-or-nothing" wait.
    axios.get(`${API_URL}/api/public/testimonials?featured_only=true`)
      .then(r => setTestimonials(r.data))
      .catch(err => console.error('testimonials error:', err?.message));
    axios.get(`${API_URL}/api/public/faqs`)
      .then(r => setFaqs(r.data))
      .catch(err => console.error('faqs error:', err?.message));
    axios.get(`${API_URL}/api/public/team`)
      .then(r => setTeam(r.data))
      .catch(err => console.error('team error:', err?.message));
  };

  const getTeamPhoto = (name) => {
    const n = name.toLowerCase();
    if (n.includes('gayle')) return TEAM_PHOTOS.gayle;
    if (n.includes('danielle')) return TEAM_PHOTOS.danielle;
    if (n.includes('gabrielle')) return TEAM_PHOTOS.gabrielle;
    return null;
  };

  // Fetch featured products when intents change
  useEffect(() => {
    if (!hasIntents) return;
    const topCategories = Object.entries(intents)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 3)
      .map(([cat]) => cat);
    fetchFeaturedProducts(topCategories);
  }, [hasIntents, JSON.stringify(intents)]);

  const fetchFeaturedProducts = async (categories) => {
    setFeaturedLoading(true);
    try {
      const params = new URLSearchParams();
      categories.forEach(c => params.append('category', c));
      const res = await axios.get(`${API_URL}/api/products?${params.toString()}`);
      // Dedupe and limit to 4
      const unique = res.data.filter((p, i, arr) => arr.findIndex(x => x.id === p.id) === i);
      setFeaturedProducts(unique.slice(0, 4));
    } catch {
      setFeaturedProducts([]);
    } finally {
      setFeaturedLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-white" data-testid="home-page">
      {/* ═══════ HERO — navy bg, Smart Site configurable ═══════ */}
      <HeroSection heroRef={heroRef} />

      {/* ═══════ STATS — teal bg ═══════ */}
      <section ref={statsRef} className="bg-[#4A90A4] py-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            <AnimatedStat value="30+" label="Years Experience" />
            <AnimatedStat value="5000+" label="Satisfied Clients" />
            <AnimatedStat value="20+" label="Partner Schools" />
            <AnimatedStat value="10000+" label="Projects Completed" />
          </div>
        </div>
      </section>

      {/* ═══════ SERVICES — white bg ═══════ */}
      <section ref={servicesRef} className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16 reveal">
            <p className="text-[#4A90A4] text-sm font-semibold tracking-[0.2em] uppercase mb-4">
              {sc(servicesContent, 'eyebrow', 'Our Services')}
            </p>
            <h2 className="text-3xl sm:text-4xl font-bold tracking-tight mb-4 text-gray-900">
              {sc(servicesContent, 'heading', 'We Specialize In You!')}
            </h2>
            <p className="text-gray-500 max-w-2xl mx-auto">
              {sc(servicesContent, 'body', 'Bringing your ideas to life requires us to test the limits of our Kre8ivity every day. If you can dream it, we will try it!')}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 stagger-grid reveal">
            {services.map((s, i) => (
              <Link
                key={i}
                to={s.href || '/services'}
                onClick={() => smart?.track('service_click', { content_category: s.name.toLowerCase().replace(/\s+/g, '-'), content_slug: s.name })}
                className="service-card group rounded-2xl overflow-hidden shadow-md border border-gray-100"
                data-testid={`service-card-${i}`}
              >
                <div className="aspect-[4/3] overflow-hidden relative">
                  <img
                    src={s.image}
                    alt={s.name}
                    className="card-image w-full h-full object-cover"
                  />
                  <div className="card-overlay absolute inset-0 bg-gradient-to-t from-[#1B3A5C]/70 to-transparent flex items-end p-6">
                    <span className="text-white font-bold text-lg">{s.name}</span>
                  </div>
                </div>
                <div className="p-6 bg-[#1B3A5C] transition-colors">
                  <h3 className="text-lg font-bold mb-2 text-white group-hover:text-[#F5A623] transition-colors">
                    {s.name}
                  </h3>
                  <p className="text-sm text-blue-100/60 leading-relaxed group-hover:text-blue-100/80 transition-colors">{s.description}</p>
                  <span className="inline-flex items-center mt-4 text-[#F5A623] text-sm font-semibold opacity-0 group-hover:opacity-100 transition-opacity">
                    Learn More <ArrowRight className="ml-1 w-4 h-4" />
                  </span>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* ═══════ FEATURED PRODUCTS — intent-driven, hidden by default ═══════ */}
      {hasIntents && featuredProducts.length > 0 && (
        <section ref={featuredRef} className="py-20 bg-gradient-to-b from-white to-[#FDF6E9]/50 relative overflow-hidden" data-testid="featured-products-section">
          <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[600px] h-[600px] bg-[#4A90A4]/[0.03] rounded-full blur-3xl pointer-events-none" />
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
            <div className="text-center mb-12 reveal">
              <div className="inline-flex items-center gap-2 px-4 py-1.5 bg-[#F5A623]/10 rounded-full mb-4">
                <Sparkles className="w-4 h-4 text-[#F5A623]" />
                <span className="text-[#F5A623] text-xs font-bold tracking-[0.15em] uppercase">Picked For You</span>
              </div>
              <h2 className="text-3xl sm:text-4xl font-bold tracking-tight text-gray-900 mb-3">
                Products You Might Love
              </h2>
              <p className="text-gray-500 max-w-lg mx-auto">
                Based on what you've been browsing, we think you'll love these.
              </p>
            </div>

            <div className="grid grid-cols-2 lg:grid-cols-4 gap-6 stagger-grid reveal">
              {featuredProducts.map((product, i) => (
                <Link
                  key={product.id}
                  to={`/product/${product.slug}`}
                  className="group bg-white rounded-2xl overflow-hidden shadow-sm border border-gray-100 hover:shadow-xl hover:-translate-y-1 transition-all duration-300"
                  data-testid={`featured-product-${i}`}
                  onClick={() => smart?.track('featured_product_click', { content_slug: product.slug })}
                >
                  <div className="aspect-square overflow-hidden bg-gray-50">
                    {product.primary_image ? (
                      <img
                        src={product.primary_image}
                        alt={product.name}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <ShoppingBag className="w-10 h-10 text-gray-200" />
                      </div>
                    )}
                  </div>
                  <div className="p-4">
                    <h3 className="font-bold text-gray-900 text-sm mb-1 line-clamp-2 group-hover:text-[#4A90A4] transition-colors">
                      {product.name}
                    </h3>
                    <div className="flex items-baseline gap-2">
                      <span className="text-lg font-bold text-[#4A90A4]">
                        ${(product.sale_price || product.base_price).toFixed(2)}
                      </span>
                      {product.sale_price && (
                        <span className="text-xs text-gray-400 line-through">${product.base_price.toFixed(2)}</span>
                      )}
                    </div>
                  </div>
                </Link>
              ))}
            </div>

            <div className="text-center mt-10">
              <Link
                to="/back-to-school"
                className="inline-flex items-center px-8 py-3 bg-[#4A90A4] text-white font-bold rounded-full hover:bg-[#3d7a8e] transition-colors"
                data-testid="featured-view-all-btn"
              >
                View All Products
                <ArrowRight className="ml-2 w-5 h-5" />
              </Link>
            </div>
          </div>
        </section>
      )}

      {/* ═══════ HOW IT WORKS — gold accent bg ═══════ */}
      <section ref={processRef} className="py-24 bg-[#FDF6E9] relative">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16 reveal">
            <p className="text-[#F5A623] text-sm font-semibold tracking-[0.2em] uppercase mb-4">
              {sc(processContent, 'eyebrow', 'Our Process')}
            </p>
            <h2 className="text-3xl sm:text-4xl font-bold tracking-tight text-gray-900">
              {sc(processContent, 'heading', 'How It Works')}
            </h2>
          </div>

          <div className="grid md:grid-cols-3 gap-8 stagger-grid reveal">
            {(processContent.items?.[0]?.data?.steps || [
              { step: '01', title: 'Get A Quote', desc: "Reach out for a personalized quote tailored to your needs. We'll discuss your vision and provide transparent pricing." },
              { step: '02', title: 'Start Your Project', desc: "Collaborate with our team to bring your ideas to life, ensuring every detail aligns with your vision." },
              { step: '03', title: 'Kre8 Your Idea', desc: 'Watch as we transform your concept into reality! Enjoy seeing your unique design come to life.' },
            ]).map((item, i) => (
              <div
                key={i}
                className="step-card relative p-8 bg-white rounded-2xl shadow-md border border-gray-100"
              >
                <span className="text-7xl font-extrabold text-[#F5A623]/10 absolute top-4 right-6 select-none">
                  {item.step}
                </span>
                <div className="step-number w-14 h-14 bg-[#4A90A4] rounded-2xl flex items-center justify-center text-white font-bold text-lg mb-5">
                  {item.step}
                </div>
                <h3 className="text-xl font-bold mb-3 text-gray-900">{item.title}</h3>
                <p className="text-gray-500 text-sm leading-relaxed">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ═══════ TEAM — navy bg ═══════ */}
      {team.length > 0 && (
        <section ref={teamRef} className="py-24 bg-[#1B3A5C] relative overflow-hidden">
          <div className="absolute inset-0 opacity-5 pattern-dots" />
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
            <div className="text-center mb-16 reveal">
              <p className="text-[#F5A623] text-sm font-semibold tracking-[0.2em] uppercase mb-4">
                Our Team
              </p>
              <h2 className="text-3xl sm:text-4xl font-bold tracking-tight text-white mb-4">
                The KYIS Dream Team
              </h2>
              <p className="text-blue-100/60 max-w-2xl mx-auto">
                From beginning to end, whether it's pricing quotes, design consultations, individual or
                bulk orders — we've got you covered.
              </p>
            </div>

            <div className="grid md:grid-cols-3 gap-8 stagger-grid reveal">
              {team.map((member, i) => (
                <div
                  key={member.id}
                  className="team-card text-center bg-white/10 backdrop-blur-sm rounded-2xl p-8 border border-white/10"
                  data-testid={`team-member-${i}`}
                >
                  <div className="team-photo w-44 h-44 mx-auto mb-6 rounded-full overflow-hidden border-4 border-[#F5A623]/40">
                    {getTeamPhoto(member.name) ? (
                      <img
                        src={getTeamPhoto(member.name)}
                        alt={member.name}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="w-full h-full bg-gradient-to-br from-[#F5A623]/30 to-[#4A90A4]/30 flex items-center justify-center">
                        <span className="text-4xl font-bold text-white">{member.name.charAt(0)}</span>
                      </div>
                    )}
                  </div>
                  <h3 className="text-xl font-bold mb-1 text-white">{member.name}</h3>
                  <p className="text-[#F5A623] text-sm font-semibold mb-1">{member.nickname}</p>
                  <p className="text-blue-200/70 text-sm mb-4">{member.title}</p>
                  {member.bio && (
                    <p className="text-sm text-blue-100/50 leading-relaxed">{member.bio}</p>
                  )}
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* ═══════ TESTIMONIALS — white bg (intent-aware) ═══════ */}
      {testimonials.length > 0 && (
        <section ref={testimonialsRef} className="py-24 bg-white" data-testid="testimonials-section">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-10 reveal">
              <p className="text-[#4A90A4] text-sm font-semibold tracking-[0.2em] uppercase mb-4">
                {sc(proofContent, 'eyebrow', 'Testimonials')}
              </p>
              <h2 className="text-3xl sm:text-4xl font-bold tracking-tight text-gray-900">
                {sc(proofContent, 'heading', 'What They Say About Kre8ives')}
              </h2>
              {orderedTestimonials.topIntent && (
                <p className="mt-3 text-xs text-[#4A90A4]/80 italic" data-testid="testimonial-intent-label">
                  Based on your interest in {INTENT_LABELS[orderedTestimonials.topIntent] || orderedTestimonials.topIntent}
                </p>
              )}
            </div>

            <div className="grid md:grid-cols-3 gap-8 stagger-grid reveal">
              {orderedTestimonials.orderedItems.slice(0, 3).map((t, i) => (
                <div
                  key={t.id}
                  className="testimonial-card p-8 bg-gray-50 rounded-2xl border border-gray-100 transition-colors"
                  data-testid={`testimonial-${i}`}
                >
                  <div className="flex mb-4">
                    {[...Array(t.rating || 5)].map((_, j) => (
                      <Star key={j} className="w-5 h-5 text-[#F5A623] fill-current" />
                    ))}
                  </div>
                  <p className="text-gray-600 leading-relaxed mb-6">"{t.content}"</p>
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-[#4A90A4] flex items-center justify-center text-white font-bold text-sm">
                      {t.author_name?.charAt(0)}
                    </div>
                    <div>
                      <p className="font-bold text-gray-900 text-sm">{t.author_name}</p>
                      {t.author_title && <p className="text-xs text-gray-400">{t.author_title}</p>}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* ═══════ FAQ — light gold bg (intent-aware: mixed by default, ordered after signals) ═══════ */}
      {faqs.length > 0 && (
        <section ref={faqRef} className="py-24 bg-[#FDF6E9]" data-testid="home-faq-section">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-10 reveal">
              <p className="text-[#F5A623] text-sm font-semibold tracking-[0.2em] uppercase mb-4">FAQ</p>
              <h2 className="text-3xl sm:text-4xl font-bold tracking-tight text-gray-900">
                {orderedFaqs.topIntent
                  ? `${INTENT_LABELS[orderedFaqs.topIntent] || orderedFaqs.topIntent} — Top Questions`
                  : 'Popular Questions'}
              </h2>
              <p className="mt-3 text-sm text-gray-500">
                {orderedFaqs.topIntent
                  ? 'Suggested based on what you\'ve been exploring.'
                  : 'A mix of what kre8ives ask us most — click a topic to dive deeper.'}
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-4 stagger-grid reveal">
              {buildHomepageFaqMix(faqs, orderedFaqs).map((faq, i) => (
                <details
                  key={faq.id}
                  className="group bg-white border border-gray-200 rounded-2xl overflow-hidden shadow-sm h-fit"
                  data-testid={`faq-${i}`}
                  onToggle={(e) => { if (e.target.open && faq.category) smart?.track?.('faq_click', { content_category: faq.category, item_id: faq.id }); }}
                >
                  <summary className="flex items-center justify-between p-5 cursor-pointer list-none font-bold text-gray-900 hover:text-[#4A90A4] transition-colors gap-3">
                    <span className="flex-1 text-sm sm:text-base">{faq.question}</span>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      {faq.category && faq.category !== 'general' && (
                        <span className="hidden md:inline-block text-[10px] uppercase tracking-wider font-semibold text-[#4A90A4]/60 bg-[#4A90A4]/5 px-2 py-1 rounded-full whitespace-nowrap">
                          {INTENT_LABELS[faq.category] || faq.category}
                        </span>
                      )}
                      <ChevronRight className="chevron-icon w-5 h-5 text-[#4A90A4]" />
                    </div>
                  </summary>
                  <div className="px-5 pb-5 -mt-1">
                    <p className="text-gray-500 leading-relaxed text-sm">{faq.answer}</p>
                  </div>
                </details>
              ))}
            </div>

            <div className="text-center mt-10 reveal">
              <Link to="/faq" className="inline-flex items-center text-[#4A90A4] font-bold hover:underline" data-testid="view-all-faqs-link">
                View All FAQs
                <ChevronRight className="ml-1 w-5 h-5" />
              </Link>
            </div>
          </div>
        </section>
      )}

      {/* ═══════ CTA — teal bg ═══════ */}
      <section ref={ctaRef} className="py-24 bg-[#4A90A4] relative overflow-hidden">
        <div className="absolute -top-20 -right-20 w-72 h-72 bg-white/5 rounded-full" />
        <div className="absolute -bottom-16 -left-16 w-64 h-64 bg-white/5 rounded-full" />
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10 reveal">
          <h2 className="text-3xl sm:text-4xl font-bold tracking-tight mb-6 text-white">
            {sc(ctaContent, 'heading', 'Ready to Bring Your Ideas to Life?')}
          </h2>
          <p className="text-white/75 text-lg mb-10 max-w-xl mx-auto">
            {sc(ctaContent, 'body', 'Let us create something special together! Get a personalized quote today.')}
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to={sc(ctaContent, 'cta_link', '/quote/builder')}
              className="inline-flex items-center justify-center px-8 py-4 bg-[#F5A623] text-gray-900 font-bold rounded-full btn-glow"
              data-testid="cta-quote-btn"
            >
              {sc(ctaContent, 'cta_text', 'Request a Quote')}
              <ArrowRight className="ml-2 w-5 h-5" />
            </Link>
            <a
              href={sc(ctaContent, 'secondary_link', 'tel:443-940-6660')}
              className="inline-flex items-center justify-center px-8 py-4 border-2 border-white text-white font-bold rounded-full hover:bg-white hover:text-[#4A90A4] transition-all"
            >
              <Phone className="mr-2 w-5 h-5" />
              {sc(ctaContent, 'secondary_text', 'Call 443-940-6660')}
            </a>
          </div>
        </div>
      </section>

      {/* ═══════ ORDER STATUS — gold bg ═══════ */}
      <section className="py-5 bg-[#F5A623]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <p className="text-gray-900 font-extrabold text-lg tracking-wide">
            TEXT 443-940-6660 TO CHECK ORDER STATUS
          </p>
        </div>
      </section>
    </div>
  );
};

export default HomePage;
